namespace $ {

	type $shm_hitalama_map_templates_snippet_meta = {
		link: string
		title: string
		file: string
		sql_file?: string
		entity_link?: string
		x: number
		y: number
		width: number
		height: number
		type?: $shm_hitalama_map_widget_def_type_name
	}

	type $shm_hitalama_map_templates_kind = 'js' | 'tsx' | 'sql'

	type $shm_hitalama_map_templates_config = {
		kind: $shm_hitalama_map_templates_kind
		title: string
		root_link: string
		default_type: $shm_hitalama_map_widget_def_type_name
		changed_at: number
	}

	type $shm_hitalama_map_templates_inline = {
		title: string
		code: string
		width?: number
		height?: number
		autorun?: boolean
	}

	export class $shm_hitalama_map_templates extends $mol_object {

		static fqn_version() {
			return 1
		}

		static fqn_version_key() {
			return 'templates_fqn_version'
		}

		static fqn_actual( map: $shm_hitalama_map ) {
			if( Number( map.store( this.fqn_version_key() ) ?? 0 ) >= this.fqn_version() ) return map

			for( const widget of map.Widgets_list( null )?.remote_list() ?? [] ) {
				const code = widget.text()
				const actual = $shm_hitalama_compat_fqn_code( code )
				if( actual !== code ) widget.text( actual )
			}

			map.store( this.fqn_version_key(), this.fqn_version() )
			return map
		}

		static projects_version() {
			return 6
		}

		@ $mol_action
		static projects_sync( map: $shm_hitalama_map, projects: readonly $shm_projects_data_record[], team?: $shm_hitalama_team | null ) {
			if( !map.can_change() ) return
			void $shm_projects_module_card
			if( team ) this.projects_members_ensure( map, team )
			const signature = JSON.stringify([ 2, projects.map( project => [ project.id, project.modules ?? [] ] ) ])
			if( map.store( 'projects_modules_signature' ) === signature ) return
			const widgets = map.Widgets_list()?.remote_list() ?? []
			const project_cards = new Map<string, $shm_hitalama_map_widget>()
			const module_cards = new Map<string, $shm_hitalama_map_widget>()
			for( const widget of widgets ) {
				const data = widget.Data()?.val() ?? {}
				if( typeof data.projects_module_id === 'string' ) {
					module_cards.set( data.projects_module_id, widget )
					if( data.projects_project_id !== undefined ) {
						const { projects_project_id: _, ...module_data } = data
						widget.Data( null )?.val( module_data )
					}
					continue
				}
				let project_id = data.projects_project_id
				if( !project_id && widget.type() === 'autoview' ) {
					const match = /view\.project_id\s*=\s*\(\)\s*=>\s*("(?:\\.|[^"\\])*")/.exec( widget.code_widget()?.text() ?? '' )
					if( match ) {
						project_id = JSON.parse( match[1] )
						widget.Data( null )?.val({ ...data, projects_project_id: project_id })
					}
				}
				if( typeof project_id === 'string' ) project_cards.set( project_id, widget )
			}
			const modules = new Map<string, { module: NonNullable<$shm_projects_data_record['modules']>[number], project: string }>()
			for( const project of projects ) for( const module of project.modules ?? [] ) {
				if( !modules.has( module.id ) ) modules.set( module.id, { module, project: module.project || project.id } )
			}
			const right = Math.max( 40, ...widgets.map( widget => widget.body_x() + widget.right_edge_x() ) ) + 180
			let index = 0
			for( const [ id, { project } ] of modules ) {
				if( module_cards.has( id ) ) continue
				const card = this.projects_view( map, id, [
					'const view = new $shm_projects_module_card',
					`view.module_id = ()=> ${JSON.stringify( id )}`,
					`view.project_id = ()=> ${JSON.stringify( project )}`,
					'return view',
				].join( '\n' ), [ right + index % 3 * 400, 364 + Math.floor( index / 3 ) * 340 ], 360, 300 )
				if( card ) {
					card.Data( null )?.val({ ...card.Data()?.val(), projects_module_id: id })
					card.auto_height( true )
					module_cards.set( id, card )
				}
				++index
			}
			const links: $shm_hitalama_map_relation_data[] = []
			for( const project of projects ) for( const module of project.modules ?? [] ) {
				const from = module_cards.get( module.id )?.link_str()
				const to = project_cards.get( project.id )?.link_str()
				if( from && to ) links.push({ id: `module-project:${module.id}:${project.id}`, from, to, kind: 'module_project', owner: 'projects' })
			}
			$shm_hitalama_map_relation.replace( map, 'projects', links )
			map.store( 'projects_modules_signature', signature )
		}

		@ $mol_action
		static team_for_map( map: $shm_hitalama_map, user_id = this.$.$giper_baza_auth.current().pass().lord().str ) {
			const teams = this.$.$mol_error_fence(
				()=> this.$.$shm_hitalama_domain.instance().teams_for_user( user_id ),
				()=> [] as $shm_hitalama_team[],
			)
			return teams.find( team =>
				team.maps_list()?.remote_list().some( item => item.link().str === map.link().str )
			) ?? ( teams.length === 1 ? teams[ 0 ] : null )
		}

		@ $mol_action
		static team_members_current_ensure( map: $shm_hitalama_map ) {
			const team = this.team_for_map( map )
			if( team ) return this.team_members_ensure( map, team )
			const id = this.$.$giper_baza_auth.current().pass().lord().str
			const profile = this.$.$shm_hitalama_profile.current()
			if( !id || !profile ) return null
			return this.team_members_records_ensure( map, [{
				id,
				name: profile.Login()?.val() || id,
				avatar: profile.Avatar()?.val()?.str ?? '',
			}] )
		}

		@ $mol_action
		static team_members_ensure( map: $shm_hitalama_map, team: $shm_hitalama_team ) {
			const members = $mol_wire_sync( this ).team_members_snapshot( team )
			return this.team_members_records_ensure( map, members )
		}

		static team_members_snapshot( team: $shm_hitalama_team ) {
			return team.member_records().map( member => {
				const id = member.User_id()?.val() || ''
				return {
					id,
					name: member.Name()?.val() || id,
					avatar: this.$.$shm_hitalama_profile.user_avatar_link( id ),
				}
			} ).filter( member => member.id )
		}

		@ $mol_action
		static team_members_records_ensure(
			map: $shm_hitalama_map,
			members: readonly { id: string, name: string, avatar: string }[],
		) {
			if( !map.can_change() ) return null
			const signature_key = 'team_members_signature'
			const signature = JSON.stringify([ 2, members ])
			// Keep the initial lookup even after creation publishes the marker.
			// Otherwise a replay skips creation and shifts every pending wire task.
			let widget = $mol_wire_sync( this ).team_members_widget_find( map )
			const current_dataset = widget?.Dataset()?.remote()
			const required = [
				[ 'Имя', 'string' ],
				[ 'ID клиента', 'string' ],
				[ 'Аватарка', 'file' ],
				[ 'Участник', 'custom' ],
			] as const
			const current_fields = current_dataset?.fields() ?? []
			const current_display = current_fields.find( field => field.title() === 'Участник' )
			const schema_ready = Boolean(
				current_dataset
					&& required.every( ([ title, type ]) =>
						current_fields.filter( field => field.title() === title ).length === 1
							&& current_fields.find( field => field.title() === title )?.type() === type
					)
					&& current_display?.custom_code()
			)
			if( widget && schema_ready && map.store( signature_key ) === signature ) return widget

			if( !widget ) {
				const plan = $mol_wire_sync( this ).team_members_widget_plan( map )
				widget = $mol_wire_sync( this ).team_members_widget_create( map, plan )
			}
			if( !widget ) return null

			const dataset = $mol_wire_sync( widget ).dataset()
			// Normalization writes into these same lists. Retain the command's
			// pre-write snapshots and isolate each potentially suspended setter.
			const records_before = $mol_wire_sync( dataset ).records()
			const fields_before = $mol_wire_sync( dataset ).fields()
			const field_list = $mol_wire_sync( dataset ).Fields( null )!
			const required_names = new Set( required.map( ([ title ])=> title ) )
			const fields = required.map( ([ title, type ])=> {
				const matches = fields_before.filter( field => field.title() === title )
				const selected = matches.find( candidate => records_before.some( record => {
					const value = record.value( candidate )
					return Array.isArray( value ) ? value.length > 0 : value !== '' && value !== null && value !== undefined
				}) ) ?? matches[ 0 ] ?? $mol_wire_sync( field_list ).make( null )
				const field = $mol_wire_sync( this ).team_members_field_canonical( selected )
				$mol_wire_sync( field ).title( title )
				$mol_wire_sync( field ).type( type )
				for( const duplicate of matches ) {
					if( duplicate.link().str === field.link().str ) continue
					for( const record of records_before ) {
						const current = record.value( field )
						if( Array.isArray( current ) ? current.length : current !== '' && current !== null && current !== undefined ) continue
						const value = record.value( duplicate )
						if( Array.isArray( value ) ? value.length : value !== '' && value !== null && value !== undefined ) {
							record.value_write_raw( field, value )
						}
					}
				}
				return field
			} )
			const extra = fields_before.filter( field => !required_names.has( field.title() as typeof required[number][0] ) )
			const normalized = [ ...fields, ...extra ]
			if( normalized.map( field => field.link().str ).join( '\n' ) !== fields_before.map( field => field.link().str ).join( '\n' ) ) {
				dataset.Fields( null )!.remote_list( normalized )
			}

			const display = fields[ 3 ]
			const display_code = [
				'const id = String( $' + "row['ID клиента'] ?? '' )",
				'const name = String( $' + "row['Имя'] ?? id )",
				'const avatar = String( $' + "row['Аватарка'] ?? '' )",
				'const badge = new $shm_hitalama_profile_badge',
				'badge.name = ()=> name',
				'badge.avatar_uri = ()=> $shm_hitalama_profile.avatar_uri_by_link( avatar )',
				'return badge',
			].join( '\n' )
			$mol_wire_sync( display ).custom_code( display_code )

			const [ name_field, id_field, avatar_field ] = fields
			for( const member of members ) {
				const record = $mol_wire_sync( this ).team_members_record_ensure( dataset, member.id, id_field )
				$mol_wire_sync( record ).value( name_field, member.name )
				$mol_wire_sync( record ).value( avatar_field, member.avatar )
			}
			map.store( signature_key, signature )
			return widget
		}

		static team_members_record_ensure(
			dataset: $shm_hitalama_map_widget_entity_table2_dataset,
			id: string,
			field: $shm_hitalama_map_widget_entity_table2_field,
		) {
			const existed = $mol_wire_sync( this ).team_members_record_find( dataset, id, field )
			if( existed ) return existed
			const initial = $mol_wire_sync( this ).team_members_record_initial( id )
			return $mol_wire_sync( dataset ).record_add( initial )
		}

		static team_members_record_find(
			dataset: $shm_hitalama_map_widget_entity_table2_dataset,
			id: string,
			field: $shm_hitalama_map_widget_entity_table2_field,
		) {
			return dataset.records().find( record => String( record.value( field ) ) === id ) ?? null
		}

		static team_members_record_initial( id: string ) { return { 'ID клиента': id } }

		static team_members_field_canonical( field: $shm_hitalama_map_widget_entity_table2_field ): $shm_hitalama_map_widget_entity_table2_field {
			// List.make can return a pawn from a detached allocation land, whereas
			// Fields.remote_list reads through Glob. Choose that same writer before
			// configuring the schema, so row initialization sees these field titles.
			return this.$.$giper_baza_glob.Pawn( field.link(), this.$.$shm_hitalama_map_widget_entity_table2_field )
		}

		static team_members_widget_plan( map: $shm_hitalama_map ) {
			const widgets = map.Widgets_list()?.remote_list() ?? []
			const bottom = Math.max( 40, ...widgets.map( item => item.body_y() + item.bottom_edge_y() ) )
			return {
				pos: [ 40, bottom + 40 ] as const,
				title: $shm_hitalama_inc_name( widgets.map( item => item.title() ), 'Участники команды' ),
			}
		}

		static team_members_widget_find( map: $shm_hitalama_map ) {
			return map.Widgets_list()?.remote_list().find( item => item.Data()?.val()?.system === 'team_members_dataset' ) ?? null
		}

		static team_members_widget_create(
			map: $shm_hitalama_map,
			plan: ReturnType< typeof $shm_hitalama_map_templates.team_members_widget_plan >,
		) {
			const widget = map.widget_add( 'table2', plan.pos, 720, 360, plan.title ) ?? null
			widget?.Data( null )?.val({ ...widget.Data()?.val(), system: 'team_members_dataset' })
			return widget
		}

		/** @deprecated Use team_members_ensure for maps of any team type. */
		static projects_members_ensure( map: $shm_hitalama_map, team: $shm_hitalama_team ) {
			return this.team_members_ensure( map, team )
		}

		static projects_version_key() {
			return 'projects_template_version'
		}

		static projects_view(
			map: $shm_hitalama_map,
			title: string,
			code: string,
			pos: readonly [ number, number ],
			width: number,
			height: number,
		) {
			const view = $mol_wire_sync( map ).widget_add( 'autoview', pos, width, height, title )
			const code_pos = $mol_wire_sync( Array ).of( pos[0] + width + 10, pos[1] ) as [number, number]
			const code_widget = $mol_wire_sync( map ).widget_add( 'code_js', code_pos, 600, height, `Code: ${ title }` )
			if( code_widget ) {
				$mol_wire_sync( code_widget ).text( code )
				$mol_wire_sync( code_widget ).view_widget( view )
			}
			if( view ) $mol_wire_sync( view ).code_widget( code_widget )
			if( code_widget ) {
				$mol_wire_sync( map ).widget_cut( code_widget.link() )
				$mol_wire_sync( code_widget ).enabled( true )
			}
			return view
		}

		static import_projects( projects: readonly $shm_projects_data_record[], land?: $giper_baza_land, team?: $shm_hitalama_team | null ) {
			// Explicit references include file-system components in the Hitalama bundle.
			void $shm_projects_card
			void $shm_projects_timeline

			const map = land
				? $mol_wire_sync( this.$.$shm_hitalama_map ).make_in_land( land, 'Проекты', 'team' )
				: $mol_wire_sync( this.$.$shm_hitalama_map ).make_new( 'Проекты', 'public_edit' )
			const plan = $mol_wire_sync( this ).projects_plan( projects )
			for( const item of plan ) {
				const card = $mol_wire_sync( this ).projects_view( map, item.title, item.code, item.pos, item.width, item.height )
				if( card && item.project_id ) $mol_wire_sync( this ).projects_card_configure( card, item.project_id )
			}
			$mol_wire_sync( this ).projects_sync( map, projects, team )
			map.store( this.projects_version_key(), this.projects_version() )
			return map
		}

		static projects_card_configure( card: $shm_hitalama_map_widget, project_id: string ) {
			$mol_wire_sync( card ).auto_height( true )
			const data = $mol_wire_sync( this ).projects_card_data( card, project_id )
			$mol_wire_sync( card.Data( null )! ).val( data )
		}

		static projects_card_data( card: $shm_hitalama_map_widget, project_id: string ) {
			return { ...card.Data()?.val(), projects_project_id: project_id }
		}

		static projects_plan( projects: readonly $shm_projects_data_record[] ) {
			const margin = 40
			const gap = 24
			const card_width = 360
			const card_height = 210
			const columns = 4
			const timeline_width = columns * card_width + ( columns - 1 ) * gap

			return [{
				title: 'Пререлизы по неделям', project_id: '',
				code: [
					'const view = new $shm_projects_timeline',
					'view.projects = ()=> $shm_projects_data.records().filter( project => !project.archived )',
					'view.week = next => {',
					'\tconst widget = $' + 'this_widget.view_widget()',
					'\tconst data = widget?.Data()?.val() ?? {}',
					"\tif( next === undefined ) return typeof data.projects_timeline_week === 'string' ? data.projects_timeline_week : ''",
					'\twidget?.Data( null )?.val({ ... data, projects_timeline_week: next })',
					'\treturn next',
					'}',
					'return view',
				].join( '\n' ),
				pos: [ margin, margin ] as readonly [number, number],
				width: timeline_width, height: 300,
			}, ...projects.map( ( project, index )=> {

				const column = index % columns
				const row = Math.floor( index / columns )
				return {
					title: project.title, project_id: project.id,
					code: [
						'const view = new $shm_projects_card',
						`view.project_id = ()=> ${ JSON.stringify( project.id ) }`,
						`view.project = ()=> $shm_projects_data.records().find( project => project.id === ${ JSON.stringify( project.id ) } ) ?? null`,
						'return view',
					].join( '\n' ),
					pos: [ margin + column * ( card_width + gap ), margin + 300 + gap + row * ( card_height + gap ) ] as readonly [number, number],
					width: card_width, height: card_height,
				}
			}) ]
		}

		static overlayed_version() {
			return 3
		}

		static overlayed_version_key() {
			return 'overlayed_template_version'
		}

		static overlayed_dto_cache = null as null | $shm_hitalama_transfer_dump
		static overlayed_dto_promise = null as null | Promise< $shm_hitalama_transfer_dump >

		static overlayed_dto_async() {
			if( this.overlayed_dto_cache ) return Promise.resolve( this.overlayed_dto_cache )
			if( this.overlayed_dto_promise ) return this.overlayed_dto_promise

			const base = '/shm/hitalama/map/templates/snippets/overlayed/'
			const uri = $shm_hitalama_ghpages_fix_link( base + 'overlayed_map.json' )
			return this.overlayed_dto_promise = fetch( uri )
				.then( response => {
					if( response.ok ) return response.text()
					throw new Error( response.statusText || `HTTP ${ response.status }` )
				} )
				.then( text => this.overlayed_dto_cache = JSON.parse( text ) as $shm_hitalama_transfer_dump )
				.catch( error => {
					this.overlayed_dto_promise = null
					throw error
				} )
		}

		static commands_version() {
			return 9
		}

		static commands_version_key() {
			return 'commands_template_version'
		}

		@ $mol_action
		static make_map(
			title: string,
			widget_type: $shm_hitalama_map_widget_def_type_name,
			templates: readonly $shm_hitalama_map_templates_inline[],
		) {
			const operation = $mol_wire_auto()
			const map = $shm_hitalama_map_history.slot(
				operation,
				`template_map:${ title }:${ widget_type }`,
				()=> $shm_hitalama_map.make_new( title, 'private' ),
			)
			map.widget_add_batch( operation ?? map, ()=> {
				let y = 40
				templates.forEach( ( template, index )=> {
					this.template_add( map, widget_type, template, y, String( index ) )
					y += ( template.height ?? 110 ) + 30
				} )
			} )

			return map
		}

		static template_add( map: $shm_hitalama_map, type: $shm_hitalama_map_widget_def_type_name, template: $shm_hitalama_map_templates_inline, y: number, creation_slot = '' ) {
			const widget = map.widget_add( type, this.template_position( y ), template.width ?? 420, template.height ?? 110, undefined, '', undefined, creation_slot )
			widget?.title( template.title )
			widget?.code( template.code )
			widget?.enabled( template.autorun ?? true )
			return widget
		}

		@ $mol_mem_key
		static template_position( y: number ) {
			return [ 40, y ] as const
		}

		@ $mol_mem
		static command_templates(): readonly $shm_hitalama_map_templates_inline[] {
			return [
				{
					title: 'Создать: Приватная карта',
					code: `$shm_hitalama_app.Root(0).add_map( 'private' )`,
					width: 600,
					height: 250,
					autorun: false,
				},
				{
					title: 'Создать: Публичная карта',
					code: `$shm_hitalama_app.Root(0).add_map( 'public_read' )`,
					width: 600,
					height: 250,
					autorun: false,
				},
				{
					title: 'Создать: Публичная редактируемая карта',
					code: `$shm_hitalama_app.Root(0).add_map( 'public_edit' )`,
					width: 600,
					height: 250,
					autorun: false,
				},
				{
					title: 'Виджет: Дашборды',
					code: `
{
const map = $shm_hitalama_app.Root(0).map()
const center = $shm_hitalama_app.Root(0).Map().Map_page().get_center_pos() ?? [ 0, 0 ]
const book_width = 1500
const book_height = 900
const left = center[0] - book_width / 2
const top = center[1] - book_height / 2
const scoped_pb_code = [
	'function auth_state( next ) {',
	'	const prev = book_view.data() || {}',
	'	if( next === undefined ) return prev',
	'	let changed = false',
	'	for( const key in next ) {',
	'		if( prev[ key ] !== next[ key ] ) changed = true',
	'	}',
	'	if( !changed ) return prev',
	'	book_view.data({ ... prev, ... next })',
	'	return book_view.data() || {}',
	'}',
	'function make_scoped_view( View, config ) {',
	'	const obj = new View',
	'	obj.$ = scoped',
	'	for( const name in config || {} ) {',
	'		const value = config[ name ]',
	'		obj[ name ] = typeof value === "function" ? value : function() { return value }',
	'	}',
	'	return obj',
	'}',
	'const ScopedPb = class extends $.$shm_ba_pb {',
	'	static _realtime = { source: null, client_id: "", topics: new Set }',
	'	static _realtime_global = new Set',
	'	static _realtime_filtered = new Set',
	'	static _realtime_live = new Set',
	'	static _realtime_cache_pending = new WeakSet',
	'	static default_url() { return $.$shm_ba_pb.default_url() }',
	'	static url() { return String( auth_state().pb_url || this.default_url() ).replace( new RegExp( "/+$" ), "" ) }',
	'	static admin_auth_state( key ) { return String( auth_state()[ key ] || "" ) }',
	'	static auth_state( key ) { return "" }',
	'	static auth_reset() {',
	'		auth_state({ token: "", user_id: "", email: "" })',
	'		this.realtime_reset()',
	'	}',
	'	static user_auth_reset() {}',
	'	static admin_auth_valid( token, next ) {',
	'	\tif( next !== undefined ) return next',
	'		if( !token ) return false',
	'		const result = $mol_error_fence(',
	'			()=> {',
	'				const response = $mol_fetch.response( this.url() + "/api/collections/_superusers/auth-refresh", {',
	'					method: "POST",',
	'					headers: this.admin_headers(),',
	'				} )',
	'				const data = response.json()',
	'				if( !response.ok() ) {',
	'					if( this.response_auth_failed( response ) ) this.auth_reset()',
	'					return false',
	'				}',
	'				if( data.token ) auth_state({ token: data.token })',
	'				if( data.record?.id ) auth_state({ user_id: data.record.id })',
	'				if( data.record?.email ) auth_state({ email: data.record.email })',
	'				return true',
	'			},',
	'			()=> false,',
	'			promise => { throw promise },',
	'		)',
	'		return Boolean( result )',
	'	}',
	'}',
	'$.$mol_mem_key( ScopedPb, "admin_auth_valid" )',
	'const scoped = $.$mol_ambient({ $shm_pb: ScopedPb, $shm_ba_pb: ScopedPb })',
].join( '\\n' )

const book_view = map.widget_add( 'autoview', [ left, top ], book_width, book_height )
book_view?.title( 'Дашборды' )
const book_code = map.widget_add( 'code_js', [ left, top ], book_width, book_height )
book_code?.title( 'Code: Дашборды' )
book_code?.enabled( false )
book_code?.code( [
	'{',
	'const book_view = ' + '$' + 'this_widget.view_widget()',
	'if( !book_view ) throw new Error( "No dashboard view widget" )',
	scoped_pb_code,
	'return make_scoped_view( scoped.$shm_dashboard_admin_book, {',
	'	auth_state: ( key, next )=> {',
	'		if( next !== undefined ) {',
	'			auth_state({ [ key ]: next })',
	'			return next || ""',
	'		}',
	'		return String( auth_state()[ key ] || "" )',
	'	},',
	'	auth_pb_url_override: ( next )=> {',
	'		if( next !== undefined ) {',
	'			auth_state({ pb_url: next })',
	'			return next || ""',
	'		}',
	'		return String( auth_state().pb_url || "" )',
	'	},',
	'	section_state_key: ()=> "admin_section_" + book_view.link_str(),',
	'} )',
	'}',
].join( '\\n' ) )
book_code?.view_widget( book_view )
book_view?.code_widget( book_code )
if( book_code ) map.widget_cut( book_code.link() )
book_code?.enabled( true )

return [ book_view ]
}
`.trim(),
					width: 600,
					height: 520,
					autorun: false,
				},
			]
		}

		static commands_update( map: $shm_hitalama_map ) {
			const widgets = map.Widgets_list(null)?.remote_list() ?? []
			let y = 40

			for( const template of this.command_templates() ) {
				const width = template.width ?? 420
				const height = template.height ?? 110
				const widget = widgets.find( ( widget: $shm_hitalama_map_widget )=> widget.code() === template.code || widget.title() === template.title )
					?? map.widget_add( 'code_js', [ 40, y ], width, height, template.title )

				if( !widget ) continue

				const enabled = template.autorun ?? true
				if( widget.title() !== template.title ) widget.title( template.title )
				if( widget.code() !== template.code ) widget.code( template.code )
				if( widget.right_edge_x() !== width ) widget.right_edge_x( width )
				if( widget.bottom_edge_y() !== height ) widget.bottom_edge_y( height )
				if( widget.enabled() !== enabled ) widget.enabled( enabled )
				y += height + 30
			}
			if( Number( map.store( this.commands_version_key() ) ?? 0 ) !== this.commands_version() ) {
				map.store( this.commands_version_key(), this.commands_version() )
			}
			return map
		}

		static import_js() {
			return this.import({
				kind: 'js',
				title: 'JS шаблоны',
				root_link: 'templates_js',
				default_type: 'code_js',
				changed_at: 1782777600000,
			})
		}

		static import_tsx() {
			return this.import({
				kind: 'tsx',
				title: 'TSX шаблоны',
				root_link: 'templates_tsx',
				default_type: 'code_ts',
				changed_at: 1777410482805,
			})
		}

		static import_sql() {
			return this.import({
				kind: 'sql',
				title: 'SQL шаблоны',
				root_link: 'templates_sql',
				default_type: 'code_sql',
				changed_at: 1777587844506,
			})
		}

		static import_commands() {
			const map = this.make_map(
				'Команды',
				'code_js',
				this.command_templates(),
			)
			map.store( this.commands_version_key(), this.commands_version() )
			return map
		}

		static commands_actual( map: $shm_hitalama_map ) {
			const version = Number( map.store( this.commands_version_key() ) ?? 0 )
			if( version >= this.commands_version() ) return this.commands_update( map )

			return this.commands_update( map )
		}

		static import_overlayed() {
			const map = $shm_hitalama_map.make_new( 'Overlay-карта', 'private' )
			return this.overlayed_apply( map )
		}

		static overlayed_update( map: $shm_hitalama_map ) {
			const version = Number( map.store( this.overlayed_version_key() ) ?? 0 )
			if( version >= this.overlayed_version() && this.overlayed_filled( map ) ) return map

			return this.overlayed_apply( map )
		}

		static overlayed_filled( map: $shm_hitalama_map ) {
			const first = map.Widgets_list(null)?.remote_list()[0]
			if( !first ) return false
			return first.right_edge_x() > 0 && first.bottom_edge_y() > 0
		}

		static overlayed_apply( map: $shm_hitalama_map ) {
			$shm_hitalama_transfer_map.make({
				map: ()=> map,
			}).deserialize_map( this.overlayed_dto() )
			map.store( this.overlayed_version_key(), this.overlayed_version() )
			map.Widgets_list(null)?.remote_list([ ...map.Widgets_list(null)!.remote_list() ])
			map.Widgets_removed_list(null)?.remote_list([ ...map.Widgets_removed_list(null)!.remote_list() ])
			return map
		}

		@ $mol_mem
		static overlayed_dto() {
			return $mol_wire_sync( this ).overlayed_dto_async()
		}

		static import( config: $shm_hitalama_map_templates_config ) {
			const map = this.$.$shm_hitalama_map.make_new( config.title, 'private' )
			// Deserialization may suspend. Keep its DTO identity across retries,
			// even though callers rebuild the small config object each time.
			const dto = $mol_wire_sync( this ).dto(
				config.kind, config.title, config.root_link, config.default_type, config.changed_at,
			)
			map.deserialize( dto )
			const widgets = map.Widgets_list(null)
			if( widgets ) widgets.remote_list([ ...widgets.remote_list() ])
			const removed = map.Widgets_removed_list(null)
			if( removed ) removed.remote_list([ ...removed.remote_list() ])
			return map
		}

		static dto(
			kind: $shm_hitalama_map_templates_kind,
			title: string,
			root_link: string,
			default_type: $shm_hitalama_map_widget_def_type_name,
			changed_at: number,
		): $shm_hitalama_transfer_dump {
			const config = { kind, title, root_link, default_type, changed_at }
			const base = `/shm/hitalama/map/templates/snippets/${ config.kind }/`
			const index_json = this.$.$mol_fetch.text( $shm_hitalama_ghpages_fix_link( base + '_index.json' ) )
			const templates = JSON.parse( index_json ) as $shm_hitalama_map_templates_snippet_meta[]
			const time = 1777410482805
			const widgets_link = config.root_link + '_widgets'
			const widgets_removed_link = config.root_link + '_widgets_removed'

			return {
				format: 'hitalama_transfer',
				root_link: config.root_link,
				nodes: [
					{
						link: config.root_link,
						title: config.title,
						class_name: '$shm_hitalama_map',
						fields: {
							Title: { kind: 'atom', value: config.title },
							Widgets: {
								kind: 'link',
								link: widgets_link,
								class_name: '$giper_baza_list_link.to',
							},
							Widgets_removed: {
								kind: 'link',
								link: widgets_removed_link,
								class_name: '$giper_baza_list_link.to',
							},
							Created_at: { kind: 'atom', value: time },
							Changed_at: { kind: 'atom', value: config.changed_at },
							Access_level: { kind: 'atom', value: 'private' },
							Version: { kind: 'atom', value: 1 },
						},
					},
					{
						link: widgets_link,
						class_name: '$giper_baza_list_link.to',
						fields: {},
						items: templates.map( template => ({
							kind: 'link',
							link: template.link,
							class_name: '$shm_hitalama_map_widget',
						}) ),
					},
					... templates.flatMap( template => this.widget_nodes( base, config, template, time ) ),
					{
						link: widgets_removed_link,
						class_name: '$giper_baza_list_link.to',
						fields: {},
						items: [],
					},
				],
			}
		}

		static widget_nodes(
			base: string,
			config: $shm_hitalama_map_templates_config,
			template: $shm_hitalama_map_templates_snippet_meta,
			time: number,
		): $shm_hitalama_transfer_node[] {
			const code = this.$.$mol_fetch.text( $shm_hitalama_ghpages_fix_link( base + template.file ) )
			const render_data = {
				title: template.title,
				type: template.type ?? config.default_type,
				text: code,
				value_refs: $shm_hitalama_map_widget_value_refs( code ),
				body_x: template.x,
				body_y: template.y,
				right_edge_x: template.width,
				bottom_edge_y: template.height,
				opacity: 1,
			}
			const fields: Record< string, $shm_hitalama_transfer_field > = {
				Render_data: { kind: 'atom', value: render_data },
				Created_at: { kind: 'atom', value: time },
				Map: {
					kind: 'link',
					link: config.root_link,
					class_name: '$shm_hitalama_map',
				},
				Text_crdt_disabled: { kind: 'atom', value: true },
				Text_atom: { kind: 'atom', value: code },
			}

			const nodes: $shm_hitalama_transfer_node[] = []
			if( template.sql_file ) {
				const entity_link = template.entity_link ?? template.link + '_entity'
				fields.Code_sql = {
					kind: 'link',
					link: entity_link,
					class_name: '$shm_hitalama_map_widget_entity_code_sql',
				}
				nodes.push({
					link: entity_link,
					class_name: '$shm_hitalama_map_widget_entity_code_sql',
					fields: {
						Sql: {
							kind: 'text',
							value: this.$.$mol_fetch.text( $shm_hitalama_ghpages_fix_link( base + template.sql_file ) ),
						},
					},
				})
			}

			return [{
				link: template.link,
				title: template.title,
				class_name: '$shm_hitalama_map_widget',
				fields,
			}, ... nodes]
		}

	}

}
